import React, { useCallback, useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import liffService from 'src/services/liff'
import { PATH as PATH_MY_PAGE } from 'src/pages/myPage'
import { LINE_STATUS } from 'src/constants/user'

import { Spin, Typography } from 'antd'
import { linkLineByLiff } from 'src/useCase/line'
import appActions from 'src/store/ui/app'
import { useAppDispatch, useAppSelector } from 'src/store'
import { useNavigate } from 'react-router-dom'
import { SOCKET_EVENT } from 'src/containers/Chat/constants'
import { createEchoConnection } from 'src/utils/echoService'
import uiActions from 'src/store/ui/notification'
import { getInfo } from 'src/useCase/user'

const { Text } = Typography

type ProcessingMessage =
  | 'linking'
  | 'opening_friendship'
  | 'confirming_friendship'

const FOLLOW_CHECK_INTERVAL_MS = 10000
const FOLLOW_CHECK_TIMEOUT_MS = 120000

const LineLinkContainer: React.FC = () => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const [message, setMessage] = useState<ProcessingMessage>('linking')
  const user = useAppSelector((state) => state.ui.app.authInfo)
  const followCheckInterval = useRef<ReturnType<typeof setInterval> | null>(
    null,
  )
  const followTimeout = useRef<ReturnType<typeof setTimeout> | null>(null)
  const friendshipRequestedRef = useRef(false)
  const isUnmountedRef = useRef(false)

  const stopFollowChecking = useCallback(() => {
    if (followCheckInterval.current) {
      clearInterval(followCheckInterval.current)
      followCheckInterval.current = null
    }

    if (followTimeout.current) {
      clearTimeout(followTimeout.current)
      followTimeout.current = null
    }
  }, [])

  const navigateToContactMethod = useCallback(() => {
    stopFollowChecking()
    navigate(PATH_MY_PAGE.CONTACT_METHOD, { replace: true })
  }, [navigate, stopFollowChecking])

  const showLinkCompleteToast = useCallback(() => {
    dispatch(
      uiActions.onNotification({
        type: 'success',
        message: 'LINE連携が完了しました。',
      }),
    )
  }, [dispatch])

  const exitWithNotification = useCallback(
    (type: 'warning' | 'error' | 'info', notificationMessage: string) => {
      dispatch(
        uiActions.onNotification({
          type,
          message: notificationMessage,
        }),
      )
      navigateToContactMethod()
    },
    [dispatch, navigateToContactMethod],
  )

  const refreshLineStatusFromApi = useCallback(async (): Promise<boolean> => {
    try {
      const res = await dispatch(linkLineByLiff()).unwrap()

      if (res.line_status === LINE_STATUS.LINKED_AND_FOLLOWED) {
        navigateToContactMethod()
        return true
      }
    } catch {
      return false
    }

    return false
  }, [dispatch, navigateToContactMethod])

  const checkFriendshipAndRefresh = useCallback(async (): Promise<boolean> => {
    try {
      const { friendFlag } = await liffService.getFriendship()

      if (friendFlag) {
        setMessage('confirming_friendship')
        return refreshLineStatusFromApi()
      }
    } catch {
      return false
    }

    return false
  }, [refreshLineStatusFromApi])

  const runFollowStatusCheck = useCallback(async (): Promise<boolean> => {
    const linkedByFriendship = await checkFriendshipAndRefresh()
    if (linkedByFriendship) return true

    try {
      const resUserInfo = await dispatch(getInfo({})).unwrap()

      if (resUserInfo?.line_status === LINE_STATUS.LINKED_AND_FOLLOWED) {
        showLinkCompleteToast()
        navigateToContactMethod()
        return true
      }
    } catch {
      return false
    }

    return false
  }, [
    checkFriendshipAndRefresh,
    dispatch,
    navigateToContactMethod,
    showLinkCompleteToast,
  ])

  const startFollowChecking = useCallback(() => {
    stopFollowChecking()
    setMessage('confirming_friendship')

    const scheduleTimeout = () => {
      followTimeout.current = setTimeout(() => {
        if (isUnmountedRef.current) return

        stopFollowChecking()
        friendshipRequestedRef.current = false

        exitWithNotification(
          'warning',
          'LINEの友だち追加が確認できませんでした。LINEアプリから再度お試しください。',
        )
      }, FOLLOW_CHECK_TIMEOUT_MS)
    }

    void runFollowStatusCheck().then((linked) => {
      if (isUnmountedRef.current || linked) return

      followCheckInterval.current = setInterval(() => {
        void runFollowStatusCheck()
      }, FOLLOW_CHECK_INTERVAL_MS)

      scheduleTimeout()
    })
  }, [exitWithNotification, runFollowStatusCheck, stopFollowChecking])

  const openLineFriendshipDialog = useCallback(async () => {
    if (friendshipRequestedRef.current) return

    friendshipRequestedRef.current = true
    setMessage('opening_friendship')

    try {
      await liffService.requestFriendship()

      const linked = await checkFriendshipAndRefresh()
      if (linked || isUnmountedRef.current) return

      startFollowChecking()
    } catch {
      friendshipRequestedRef.current = false

      if (!liffService.isInClient()) {
        exitWithNotification(
          'info',
          '友だち追加はLINEアプリ内で行ってください。LINEアプリから再度お試しください。',
        )
        return
      }

      exitWithNotification(
        'warning',
        '友だち追加画面を開けませんでした。LINE Developers ConsoleでLIFFのサイズをFullにし、公式アカウントを連携してください。',
      )
    }
  }, [checkFriendshipAndRefresh, exitWithNotification, startFollowChecking])

  const handleOauthCancel = () => {
    const params = new URLSearchParams(window.location.search)
    const oauthError = params.get('error')

    if (oauthError === 'ACCESS_DENIED') {
      sessionStorage.removeItem('line_link_screen_visited')
      window.history.replaceState({}, document.title, window.location.pathname)
      navigate(PATH_MY_PAGE.CONTACT_METHOD, { replace: true })
      return true
    }

    return false
  }

  const handleLiffLogin = () => {
    const visited =
      sessionStorage.getItem('line_link_screen_visited') === 'true'

    if (visited) {
      sessionStorage.removeItem('line_link_screen_visited')
      return navigate(PATH_MY_PAGE.CONTACT_METHOD)
    }

    sessionStorage.setItem('line_link_screen_visited', 'true')
    liffService.login()
  }

  const handleLineLinkError = useCallback(
    (error: any) => {
      if (
        error?.code === 'errors.invalid_authorization_line_code' ||
        error?.code === 'errors.duplicate_line_id'
      ) {
        if (error?.code === 'errors.invalid_authorization_line_code') {
          liffService.logout()
        }

        return navigate(PATH_MY_PAGE.CONTACT_METHOD)
      }

      const errorLineStatus = error?.line_status ?? error?.status

      if (errorLineStatus === LINE_STATUS.LINKED_NOT_FOLLOWED) {
        void openLineFriendshipDialog()
      }
    },
    [navigate, openLineFriendshipDialog],
  )

  const handleLineLink = useCallback(async () => {
    try {
      const res = await dispatch(linkLineByLiff()).unwrap()

      if (res.line_status === LINE_STATUS.LINKED_AND_FOLLOWED) {
        return navigateToContactMethod()
      }

      if (res.line_status === LINE_STATUS.LINKED_NOT_FOLLOWED) {
        await openLineFriendshipDialog()
      }
    } catch (error: any) {
      handleLineLinkError(error)
    }
  }, [
    dispatch,
    handleLineLinkError,
    navigateToContactMethod,
    openLineFriendshipDialog,
  ])

  const initLiffFlow = async () => {
    try {
      await liffService.init()

      if (!liffService.isLogin) {
        handleLiffLogin()
        return
      }

      await handleLineLink()
    } catch {
      exitWithNotification('error', 'LINE連携の初期化に失敗しました。')
    }
  }

  useEffect(() => {
    isUnmountedRef.current = false

    if (handleOauthCancel()) return
    initLiffFlow()

    return () => {
      isUnmountedRef.current = true
      stopFollowChecking()
    }
  }, [])

  useEffect(() => {
    if (!user?.id) return

    const echo = createEchoConnection()
    const channelName = `private-user.${user.id}`

    echo.channel(channelName).listen(SOCKET_EVENT.LINE_FOLLOWED, ({ data }) => {
      if (data?.status === LINE_STATUS.LINKED_AND_FOLLOWED) {
        stopFollowChecking()
        friendshipRequestedRef.current = false

        dispatch(appActions.patchAuthInfo({ line_status: data.status }))
        showLinkCompleteToast()
        navigateToContactMethod()
      }
    })

    return () => {
      echo.leaveChannel(channelName)
    }
  }, [
    user?.id,
    dispatch,
    navigateToContactMethod,
    showLinkCompleteToast,
    stopFollowChecking,
  ])

  const messageText = {
    linking: 'LINE連携を処理しています…',
    opening_friendship: '友だち追加画面を開いています…',
    confirming_friendship: '友だち追加を確認しています…',
  }[message]

  return (
    <Container>
      <Card>
        <Spin size="large" />
        <Text type="secondary" style={{ marginTop: 16 }}>
          {messageText}
        </Text>
      </Card>
    </Container>
  )
}

export default LineLinkContainer

const Container = styled.div`
  display: flex;
  justify-content: center;
  background: #f6f6f6;
  min-height: 100vh;
  align-items: center;
  padding: 20px;
`

const Card = styled.div`
  width: 100%;
  max-width: 420px;
  background: white;
  padding: 32px 24px;
  border-radius: 12px;
  text-align: center;
`
